import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, CalendarDays, Info, ChevronRight } from 'lucide-react';
import { salesTargetsAPI } from '../../services/api';
import { downloadXlsx } from '../../utils/download';
import Modal from '../../components/Modal';
import { SearchInput } from '../../components/SearchInput';
import PaginationFooter from '../../components/PaginationFooter';
import { usePagination } from '../../hooks/usePagination';
import { PageErrorBanner } from '../../components/PageErrorBanner';
import { BlockLoader } from '../../components/loading';
import { NumSortTh } from '../../utils/sortableHeader';
import SalesCasesModal from '../../components/SalesCasesModal';
import { formatIsoDay } from '../../utils/dateHelpers';
import {
  coordKey, formatNumber, formatStoreDays,
  type MonthComputation, type SalesTargetConfig, type ComputedEmployeeLine,
} from './types';

/**
 * What each promoter is actually held to — and what they sold.
 *
 * ── THE NUMBER THIS TAB EXISTS TO SHOW ───────────────────────────────────────
 * Nobody types an employee target. It is worked out from the store's target, the
 * store's multiplier and the deployment history, DAY BY DAY: a promoter who
 * moves on the 15th carries 14 days of one shop and 17 of the next. The
 * day-by-day view behind each row is where that becomes legible, and it is the
 * only honest answer to "why is my target this number".
 *
 * ── AND THE ONE IT EXISTS TO REFUSE ──────────────────────────────────────────
 * The banner at the top is not a warning, it is a blocker in spirit: a store
 * handing out more than its own target has a multiplier that does not match its
 * headcount, and every figure derived from it is wrong. It leads, it names the
 * fix, and it offers the list as a file. It starts collapsed to one line of
 * counts by kind: a month with a few empty stores repeats the same sentence
 * hundreds of times, which pushed the table a screen down.
 */

interface Props {
  projectId: number;
  month: string;
  config: SalesTargetConfig;
}

interface DayLine {
  day: string;
  storeCode: string;
  storeName: string;
  productLabel: string;
  value: number;
  /** A day after they left, which the target runs on to. */
  afterLeaving?: boolean;
}

/**
 * The days a Daily Pace is over: those deployed, and for somebody who left, the days
 * the whole-month target runs on to as well, so the pace is the month's rate and not
 * that figure divided by the few days they stayed. An older payload has no paceDays.
 */
const paceDaysOf = (l: ComputedEmployeeLine): number => l.paceDays ?? l.deployedDays;

/**
 * Whether this line's target really runs on past their last day. Somebody can have left
 * and carry nothing here: they left on the month's last day, they moved to another
 * project first, or the store they left from has no target for this product.
 */
const carriesOn = (l: ComputedEmployeeLine): boolean => l.contributions.some((c) => (c.carriedDays ?? 0) > 0);

type SortCol = 'code' | 'name' | 'target' | 'pace' | 'days' | 'sold' | 'achievement';

const PAGE_SIZES = [
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

function Tile({ label, value, hint, tone = 'plain' }: {
  label: string; value: string; hint?: string; tone?: 'plain' | 'warn';
}) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${tone === 'warn' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tone === 'warn' ? 'text-amber-900' : 'text-gray-900'}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export default function ResultsTab({ projectId, month, config }: Props) {
  const [data, setData] = useState<MonthComputation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * undefined = not chosen yet. The first request must already carry a product:
   * `employeeLines` is CAPPED server-side, so an unfiltered load on a project
   * with thousands of promoters returns a truncated slice and filtering it here
   * would silently show only the promoters who happened to fall inside the cap.
   */
  const [selectedCoord, setSelectedCoord] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortKey, setSortKey] = useState<SortCol>('target');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [detailFor, setDetailFor] = useState<ComputedEmployeeLine | null>(null);
  const [detailDays, setDetailDays] = useState<DayLine[] | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Why the day-by-day could not load. Kept apart from an empty list: "no
  // deployment recorded" is a statement about the promoter, and must not be what
  // a refused or failed request says.
  const [detailError, setDetailError] = useState<string | null>(null);
  const [casesOpen, setCasesOpen] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await salesTargetsAPI.getComputed(projectId, {
        month,
        // Scoped server-side. `coordinates` comes back unfiltered either way, so
        // the product dropdown still lists everything.
        coordKeys: selectedCoord || undefined,
      });
      const computed: MonthComputation = res.data.data;
      setData(computed);
      setSelectedCoord((prior) => (
        prior && computed.coordinates.some((c) => coordKey(c.coord) === prior)
          ? prior
          : computed.coordinates.length > 0 ? coordKey(computed.coordinates[0].coord) : ''
      ));
    } catch (err: any) {
      setError(err.response?.data?.message || `Could not work out the figures for ${month}.`);
    } finally {
      setLoading(false);
    }
  }, [projectId, month, selectedCoord]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, selectedCoord]);

  const soldByKey = useMemo(() => {
    const map = new Map<string, { sold: number; pct: number | null }>();
    (data?.achievement ?? []).forEach((a) => {
      map.set(`${a.employeeCode}::${coordKey(a.coord)}`, { sold: a.sold, pct: a.achievementPct });
    });
    return map;
  }, [data]);

  /**
   * What the warnings banner says while collapsed: a count per kind, so a closed
   * banner still says what is wrong and how much. Targets are per product, so one
   * empty store with a target on six products is six warnings — the store count
   * is the real size of it.
   */
  const warningSummary = useMemo(() => {
    const byKind = new Map<string, { count: number; stores: Set<string> }>();
    (data?.warnings ?? []).forEach((w) => {
      const entry = byKind.get(w.kind) ?? { count: 0, stores: new Set<string>() };
      entry.count += 1;
      entry.stores.add(w.storeId);
      byKind.set(w.kind, entry);
    });
    const plural = (n: number, word: string) => `${formatNumber(n)} ${word}${n === 1 ? '' : 's'}`;
    // Most serious first: a target that rounds to zero is wrong for people who
    // ARE deployed, while an unstaffed store only has nobody to carry its target.
    const order = ['rounds_to_zero', 'no_multiplier_rule', 'unstaffed'];
    const rank = (kind: string) => (order.includes(kind) ? order.indexOf(kind) : order.length);
    return [...byKind.entries()]
      .sort(([a], [b]) => rank(a) - rank(b))
      .map(([kind, { count, stores }]) => {
        switch (kind) {
          case 'rounds_to_zero': return `${plural(count, 'target')} rounding to zero for every promoter`;
          case 'no_multiplier_rule': return `${plural(count, 'target')} with no multiplier rule`;
          case 'unstaffed': return `${plural(count, 'target')} at ${plural(stores.size, 'store')} with nobody deployed`;
          default: return `${formatNumber(count)} other`;
        }
      });
  }, [data]);

  const rows = useMemo(() => {
    const lines = (data?.employeeLines ?? []).filter((l) => coordKey(l.coord) === selectedCoord);

    // Somebody can SELL a product they were never targeted on — the store simply
    // had no target for it. Those rows exist in `achievement` but not in
    // `employeeLines`, so leaving them out made the Sold tile disagree with the
    // sum of the Sold column. Appended with a zero target, so the table
    // reconciles and the person is visible rather than quietly missing.
    const present = new Set(lines.map((l) => `${l.employeeCode}::${coordKey(l.coord)}`));
    const orphans: ComputedEmployeeLine[] = (data?.achievement ?? [])
      .filter((a) => coordKey(a.coord) === selectedCoord && a.target === 0 && a.sold > 0)
      .filter((a) => !present.has(`${a.employeeCode}::${coordKey(a.coord)}`))
      .map((a) => ({
        employeeCode: a.employeeCode,
        employeeName: a.employeeName,
        coord: a.coord,
        productLabel: a.productLabel,
        monthlyTarget: 0,
        rawMonthlyTarget: 0,
        dayTarget: 0,
        averageDayTarget: 0,
        deployedDays: 0,
        contributions: [],
      }));
    const all = [...lines, ...orphans];

    const q = search.trim().toLowerCase();
    const filtered = q
      ? all.filter((l) => l.employeeCode.toLowerCase().includes(q)
        || (l.employeeName ?? '').toLowerCase().includes(q))
      : all;

    const value = (l: ComputedEmployeeLine): string | number => {
      const sold = soldByKey.get(`${l.employeeCode}::${coordKey(l.coord)}`);
      switch (sortKey) {
        case 'code': return l.employeeCode;
        case 'name': return l.employeeName ?? l.employeeCode;
        case 'target': return l.monthlyTarget;
        case 'pace': return paceDaysOf(l) > 0 ? l.rawMonthlyTarget / paceDaysOf(l) : -1;
        case 'days': return l.deployedDays;
        case 'sold': return sold?.sold ?? -1;
        case 'achievement': return sold?.pct ?? -1;
        default: return 0;
      }
    };
    return [...filtered].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      const cmp = typeof av === 'string' && typeof bv === 'string'
        ? av.localeCompare(bv, undefined, { numeric: true })
        : Number(av) - Number(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, selectedCoord, search, sortKey, sortDir, soldByKey]);

  const { totalPages, safePage, paginatedData } = usePagination(rows, page, pageSize);

  const onSort = (col: SortCol) => {
    if (col === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(col); setSortDir(col === 'code' || col === 'name' ? 'asc' : 'desc'); }
  };

  const openDetail = async (line: ComputedEmployeeLine) => {
    setDetailFor(line);
    setDetailDays(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await salesTargetsAPI.getEmployeeDetail(projectId, line.employeeCode, month);
      setDetailDays(
        (res.data.data.days ?? []).filter((d: DayLine & { coord?: any }) =>
          coordKey(d.coord) === coordKey(line.coord)),
      );
    } catch (err: any) {
      setDetailDays([]);
      // 403: the promoter is outside the viewer's reporting line (the server
      // checks, since the route itself is open in production).
      setDetailError(err.response?.data?.message || 'Could not load the day-by-day figures.');
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadEmployees = async () => {
    try {
      const res = await salesTargetsAPI.downloadEmployeeTargets(projectId, month);
      downloadXlsx(`employee_targets_${month}.xlsx`, res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not build the download.');
    }
  };

  const downloadValidation = async () => {
    try {
      const res = await salesTargetsAPI.downloadValidation(projectId, month);
      downloadXlsx(`sales_target_validation_${month}.xlsx`, res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not build the download.');
    }
  };

  if (loading) {
    return (
      <BlockLoader className="rounded-xl border border-gray-200 bg-white py-20" />
    );
  }
  if (!data) return error ? <PageErrorBanner message={error} /> : null;

  const totals = data.achievementTotals;
  const hasSales = (totals?.soldTotal ?? 0) > 0;
  const productsWithTargets = data.coverage.byProduct
    .filter((p) => p.explicit + p.onDefault > 0)
    .map((p) => p.productLabel);

  return (
    <div className="space-y-4">
      {error && <PageErrorBanner message={error} />}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label={config.sourceScope === 'store' ? 'Store targets' : 'Promoter targets'}
          value={formatNumber(Math.round(data.totals.storeTargetTotal))}
          hint={`across ${formatNumber(data.coordinates.length)} product${data.coordinates.length === 1 ? '' : 's'}`}
        />
        <Tile
          label="Carried by promoters"
          value={formatNumber(Math.round(data.totals.employeeTargetTotal))}
          hint={`${data.totalDays} days in ${month}`}
        />
        {hasSales ? (
          <>
            <Tile label="Sold" value={formatNumber(Math.round(totals?.soldTotal ?? 0))} />
            <Tile
              label="Achievement"
              value={totals?.achievementPct === null || totals?.achievementPct === undefined
                ? '—' : `${totals.achievementPct}%`}
              hint={`${formatNumber(totals?.employeesOnTarget ?? 0)} on target, ${formatNumber(totals?.employeesBehind ?? 0)} behind`}
            />
          </>
        ) : (
          <>
            <Tile
              label="Reached nobody"
              value={formatNumber(Math.round(data.totals.unallocatedTotal))}
              tone={data.totals.unallocatedTotal > 0 ? 'warn' : 'plain'}
              hint="Store target for empty days no one carries"
            />
            <Tile label="Sold" value="—" hint="Record sales on the Sales tab" />
          </>
        )}
      </div>

      {data.warnings.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50">
          {/* Collapsed by default to one line. The counts by kind stay visible, so
              closing it hides the list, not the problem. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2.5">
            <button
              type="button"
              onClick={() => setWarningsOpen((open) => !open)}
              aria-expanded={warningsOpen}
              aria-controls="results-warnings-list"
              title={warningsOpen ? 'Hide the list' : 'Show the list'}
              className="flex min-w-[14rem] flex-1 items-start gap-2 rounded text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            >
              <ChevronRight
                className={`mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700 transition-transform ${warningsOpen ? 'rotate-90' : ''}`}
              />
              <span className="min-w-0">
                <span className="font-semibold text-amber-900">
                  {formatNumber(data.warnings.length)} thing{data.warnings.length === 1 ? '' : 's'} worth a look
                </span>
                <span className="text-amber-800"> — {warningSummary.join(' · ')}</span>
              </span>
            </button>
            <button
              type="button"
              onClick={downloadValidation}
              className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download the full list
            </button>
          </div>
          <div id="results-warnings-list" hidden={!warningsOpen} className="border-t border-amber-200 px-4 py-3">
            <ul className="space-y-1 text-sm text-amber-800">
              {data.warnings.slice(0, 8).map((w, i) => (
                <li key={`${w.storeId}-${i}`}>
                  <span className="font-medium">{w.storeCode || w.productLabel}</span>
                  {w.storeCode && w.productLabel ? ` · ${w.productLabel}` : ''} — {w.message}
                </li>
              ))}
            </ul>
            {data.warnings.length > 8 && (
              <p className="mt-1 text-xs text-amber-700">
                and {data.warnings.length - 8} more.
              </p>
            )}
          </div>
        </div>
      )}

      {data.truncated && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          This month has more rows than the screen will carry, so the table below is cut short.
          Use &ldquo;Download every product&rdquo; for the complete picture.
        </p>
      )}

      {data.coordinates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
          <h3 className="text-lg font-medium text-gray-600">No products yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Add them in Master Data → Products and targets can be set against them.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="res-product">
                  Product
                </label>
                <select
                  id="res-product"
                  value={selectedCoord ?? ''}
                  onChange={(e) => setSelectedCoord(e.target.value)}
                  className="min-w-[16rem] h-[38px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  {/* Each option says how many promoters carry a target for it.
                      Without that, a product with nothing set looks identical to
                      one where the page failed to load. `byProduct` is the whole
                      catalogue even when the request was scoped to one product,
                      so these counts stay correct after switching. */}
                  {data.coordinates.map((c) => {
                    const key = coordKey(c.coord);
                    const stats = data.coverage.byProduct.find(
                      (p) => `${p.productId}::${p.skuId ?? ''}` === key,
                    );
                    const withTarget = stats ? stats.explicit + stats.onDefault : null;
                    return (
                      <option key={key} value={key}>
                        {c.label}
                        {withTarget === null ? ''
                          : withTarget > 0
                            ? ` — ${withTarget.toLocaleString()} with a target`
                            : ' — no targets set'}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="w-64">
                <SearchInput size="md" value={search} onChange={setSearch} placeholder="Search promoter" />
              </div>
            </div>
            <button
              type="button"
              onClick={downloadEmployees}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Download every product
            </button>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <NumSortTh label="Promoter" col="code" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="left" />
                    <NumSortTh label="Name" col="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="left" />
                    <NumSortTh label="Month target" col="target" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <NumSortTh
                      label={(
                        <span title="Month target ÷ days deployed; for somebody who left, ÷ the days to month end. Shown to one decimal because a whole number would not multiply back to the month target.">
                          Daily pace
                        </span>
                      )}
                      col="pace" sortKey={sortKey} sortDir={sortDir} onSort={onSort}
                    />
                    <NumSortTh label="Days" col="days" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    {hasSales && (
                      <>
                        <NumSortTh label="Sold" col="sold" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                        <NumSortTh label="Achieved" col="achievement" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      </>
                    )}
                    <th className="bg-gray-50 px-3 py-2 text-left">Stores</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedData.map((l) => {
                    const sold = soldByKey.get(`${l.employeeCode}::${coordKey(l.coord)}`);
                    const behind = sold?.pct !== null && sold?.pct !== undefined && sold.pct < 100;
                    return (
                      <tr
                        key={`${l.employeeCode}-${coordKey(l.coord)}`}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => openDetail(l)}
                        title="See the day-by-day breakdown"
                      >
                        <td className="px-3 py-2 font-medium text-gray-900">{l.employeeCode}</td>
                        <td className="px-3 py-2 text-gray-600">{l.employeeName ?? '—'}</td>
                        <td
                          className="px-3 py-2 text-right"
                          title={l.leftOn && (carriesOn(l) || config.sourceScope === 'employee')
                            ? `Left on ${formatIsoDay(l.leftOn)}: the target stays for the whole month`
                            : undefined}
                        >
                          {l.monthlyTarget > 0 ? (
                            <span className="font-semibold text-gray-900">
                              {formatNumber(l.monthlyTarget)}
                            </span>
                          ) : (
                            <span
                              className="text-xs text-amber-700"
                              title="Sold this product without a target for it"
                            >
                              no target
                            </span>
                          )}
                        </td>
                        <td
                          className="px-3 py-2 text-right text-gray-600"
                          title={paceDaysOf(l) > 0
                            ? `${l.monthlyTarget} ÷ ${paceDaysOf(l)} days ${l.paceDays ? 'to month end' : 'deployed'}`
                            : 'Not deployed this month'}
                        >
                          {paceDaysOf(l) > 0
                            ? (Math.round((l.rawMonthlyTarget / paceDaysOf(l)) * 10) / 10).toFixed(1)
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">{l.deployedDays}</td>
                        {hasSales && (
                          <>
                            <td className="px-3 py-2 text-right text-gray-900">
                              {formatNumber(sold?.sold ?? 0)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {sold?.pct === null || sold?.pct === undefined ? (
                                <span className="text-gray-400">—</span>
                              ) : (
                                <span className={behind ? 'font-semibold text-amber-700' : 'font-semibold text-gray-900'}>
                                  {sold.pct}%
                                </span>
                              )}
                            </td>
                          </>
                        )}
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {l.contributions.length === 0
                            ? '—'
                            : formatStoreDays(l.contributions)}
                        </td>
                      </tr>
                    );
                  })}
                  {paginatedData.length === 0 && (
                    <tr>
                      <td colSpan={hasSales ? 8 : 6} className="px-4 py-12 text-center text-sm text-gray-500">
                        No promoter carries a target for this product in {month}. Either no target
                        has been set for it, or nobody was deployed where one is.
                        {productsWithTargets.length > 0 && (
                          <span className="mt-1 block">
                            Targets do exist for {productsWithTargets.join(', ')}.
                          </span>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {rows.length > 0 && (
              <PaginationFooter
                safePage={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZES}
                totalRows={rows.length}
                onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
                onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
                onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            )}
          </div>
        </>
      )}

      <Modal
        isOpen={detailFor !== null}
        onClose={() => { setDetailFor(null); setDetailDays(null); }}
        title={detailFor ? `${detailFor.employeeName ?? detailFor.employeeCode} · ${detailFor.productLabel}` : ''}
        size="xl"
      >
        {detailFor && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>
                A store&apos;s monthly target is spread evenly across the {data.totalDays} days of{' '}
                {month}. This promoter earns a share for each day they stood in that store, so a
                move mid-month splits the target between the two without anyone recalculating
                anything. The daily figures below are unrounded and add up to{' '}
                <strong>{formatNumber(detailFor.monthlyTarget)}</strong> after rounding once at the
                end.
                {detailFor.leftOn && (
                  <>
                    {' '}Left on {formatIsoDay(detailFor.leftOn)}
                    {carriesOn(detailFor)
                      ? ': the target stays for the whole month, so the days after that count at their last store.'
                      : '.'}
                  </>
                )}
              </span>
            </div>

            {hasSales && (
              <button
                type="button"
                onClick={() => setCasesOpen(true)}
                className="text-sm font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700"
              >
                See {detailFor.employeeName ?? detailFor.employeeCode}&apos;s individual {detailFor.productLabel} sales
                and whether each counted
              </button>
            )}

            <div className="grid grid-cols-3 gap-3">
              <Tile label="Month target" value={formatNumber(detailFor.monthlyTarget)} />
              <Tile label="Daily pace" value={formatNumber(detailFor.averageDayTarget)} hint="to finish the month" />
              <Tile label="Days deployed" value={String(detailFor.deployedDays)} />
            </div>

            {detailFor.contributions.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Where it came from
                </p>
                <div className="space-y-1">
                  {detailFor.contributions.map((c) => (
                    <div key={c.storeId} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm">
                      <span>
                        <span className="font-medium text-gray-900">{c.storeCode || c.storeId}</span>
                        <span className="ml-2 text-gray-600">{c.storeName}</span>
                        {c.origin === 'default' && (
                          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            on a default
                          </span>
                        )}
                      </span>
                      <span className="text-gray-700">
                        {c.days} day{c.days === 1 ? '' : 's'}
                        {c.carriedDays ? ` + ${c.carriedDays} after leaving` : ''} · {Math.round(c.value * 100) / 100}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <CalendarDays className="h-3.5 w-3.5" />
                Day by day
              </p>
              {detailLoading ? (
                <BlockLoader className="py-8" />
              ) : detailError ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-6 text-center text-sm text-amber-800">
                  {detailError}
                </p>
              ) : (detailDays ?? []).length === 0 ? (
                <p className="rounded-lg border border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                  No deployment recorded for this promoter in {month}.
                </p>
              ) : (
                <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Store</th>
                        <th className="px-3 py-2 text-right">Target for the day</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(detailDays ?? []).map((d, i) => {
                        const prior = (detailDays ?? [])[i - 1];
                        const moved = prior && prior.storeCode !== d.storeCode;
                        return (
                          <tr key={`${d.day}-${d.storeCode}`} className={moved ? 'border-t-2 border-primary-200' : ''}>
                            <td className="px-3 py-1.5 text-gray-700">{d.day}</td>
                            <td className="px-3 py-1.5">
                              <span className={moved ? 'font-semibold text-primary-700' : 'text-gray-700'}>
                                {d.storeCode || '—'}
                              </span>
                              {moved && <span className="ml-2 text-xs text-primary-600">moved here</span>}
                              {d.afterLeaving && <span className="ml-2 text-xs text-gray-500">after leaving</span>}
                            </td>
                            <td className="px-3 py-1.5 text-right text-gray-700">
                              {Math.round(d.value * 100) / 100}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <SalesCasesModal
        isOpen={casesOpen && detailFor !== null}
        onClose={() => setCasesOpen(false)}
        projectId={projectId}
        month={month}
        employeeCode={detailFor?.employeeCode ?? null}
        productId={detailFor?.coord.productId ?? null}
        skuId={detailFor?.coord.skuId ?? null}
        productLabel={detailFor?.productLabel ?? null}
      />
    </div>
  );
}
